import React, { useState } from 'react';
import { Button, Drawer, Table, Tag, Typography, Space, Alert, InputNumber, message, Empty } from 'antd';
import { CopyOutlined, WarningOutlined } from '@ant-design/icons';
import ApiService from '../../APIServices/ApiService';

const { Text, Paragraph } = Typography;

/**
 * Orders that were created twice for one checkout, and the eSIMs they bought.
 *
 * A double-submitted checkout wrote two orders seconds apart, and for an order
 * containing an eSIM the second one PURCHASED A SECOND eSIM from the provider.
 * This lists them with the provider's own references so a refund can actually
 * be claimed, rather than described.
 *
 * It changes nothing. Which of a pair to keep is a judgement about a real
 * customer's order, and the eSIM may already have been installed.
 */
const DuplicateOrders = () => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [windowMinutes, setWindowMinutes] = useState(10);
    const [days, setDays] = useState(90);

    const scan = async () => {
        setLoading(true);
        try {
            const res = await ApiService.findDuplicateWebsiteOrders({ windowMinutes, days });
            setData(res);
            setOpen(true);
        } catch (e) {
            message.error(e?.response?.data?.message || 'Could not scan for duplicates.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * The claim, as plain text.
     *
     * Only the orders marked as waste and only their eSIMs, because that is
     * what the provider is being asked to refund — pasting the whole report
     * would include the orders the customer actually made.
     */
    const claimText = () => {
        if (!data) return '';
        const lines = ['Duplicate eSIM purchases — refund request', ''];
        for (const g of data.duplicates) {
            const wasted = g.orders.filter((o) => !o.keep);
            if (wasted.every((o) => o.esims.length === 0)) continue;
            lines.push(`Customer: ${g.email}`);
            lines.push(`  Kept order: ${g.orders.find((o) => o.keep)?.transactionId}`);
            for (const o of wasted) {
                for (const e of o.esims) {
                    lines.push(
                        `  DUPLICATE: order ${o.transactionId} · ${e.packageName || e.packageCode}` +
                        `${e.orderNo ? ` · provider order ${e.orderNo}` : ''}` +
                        `${e.iccids.length ? ` · ICCID ${e.iccids.join(', ')}` : ''}`,
                    );
                }
            }
            lines.push('');
        }
        lines.push(`Total duplicate eSIMs: ${data.duplicateEsims}`);
        return lines.join('\n');
    };

    const copyClaim = async () => {
        try {
            await navigator.clipboard.writeText(claimText());
            message.success('Refund list copied.');
        } catch {
            message.error('Could not copy. Select the text below instead.');
        }
    };

    const columns = [
        {
            title: 'Customer',
            dataIndex: 'email',
            render: (email, g) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{email}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {g.count} orders · €{(g.totalAmount || 0).toFixed(2)} each
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Orders',
            key: 'orders',
            render: (_, g) => (
                <Space direction="vertical" size={2}>
                    {g.orders.map((o) => (
                        <div key={o.transactionId}>
                            {/* The first is the customer's real order; the rest
                                are what the double submit created. */}
                            <Tag color={o.keep ? 'green' : 'red'}>{o.keep ? 'KEEP' : 'DUPLICATE'}</Tag>
                            <Text code style={{ fontSize: 12 }}>{o.transactionId.slice(0, 8).toUpperCase()}</Text>
                            <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                                {new Date(o.createdAt).toLocaleString()} · {o.status}
                            </Text>
                            {o.esims.map((e, i) => (
                                <div key={i} style={{ marginLeft: 24, fontSize: 12 }}>
                                    <Text type="secondary">
                                        {e.packageName || e.packageCode}
                                        {e.orderNo ? ` · provider ${e.orderNo}` : ''}
                                        {e.iccids.length ? ` · ICCID ${e.iccids.join(', ')}` : ''}
                                    </Text>
                                </div>
                            ))}
                        </div>
                    ))}
                </Space>
            ),
        },
        {
            title: 'Wasted eSIMs',
            dataIndex: 'duplicateEsims',
            width: 120,
            align: 'right',
            render: (n) => (n > 0 ? <Tag color="red">{n}</Tag> : <Tag>0</Tag>),
        },
    ];

    return (
        <>
            <Button icon={<WarningOutlined />} loading={loading} onClick={scan}>
                Find duplicates
            </Button>

            <Drawer
                open={open}
                width={900}
                onClose={() => setOpen(false)}
                title="Orders created twice"
                extra={
                    data?.duplicateEsims > 0 && (
                        <Button icon={<CopyOutlined />} onClick={copyClaim}>Copy refund list</Button>
                    )
                }
            >
                <Space style={{ marginBottom: 16 }} wrap>
                    <span>Within</span>
                    <InputNumber min={1} max={120} value={windowMinutes} onChange={setWindowMinutes} addonAfter="min" />
                    <span>over the last</span>
                    <InputNumber min={1} max={730} value={days} onChange={setDays} addonAfter="days" />
                    <Button onClick={scan} loading={loading}>Rescan</Button>
                </Space>

                {data && (
                    <>
                        {data.duplicateEsims > 0 ? (
                            <Alert
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                                message={`${data.duplicateEsims} eSIM${data.duplicateEsims === 1 ? '' : 's'} bought that nobody ordered`}
                                description={`Across ${data.groups} duplicated checkout${data.groups === 1 ? '' : 's'}, from ${data.scanned} PAID orders scanned. Copy the refund list and send it to eSIM Access — each line has the provider order number and ICCID.`}
                            />
                        ) : (
                            <Alert
                                type="success"
                                showIcon
                                style={{ marginBottom: 16 }}
                                message="No duplicate eSIM purchases found"
                                description={`${data.scanned} paid orders scanned over ${data.days} days. Pending orders are not counted: nothing was bought for them.`}
                            />
                        )}

                        <Paragraph type="secondary" style={{ fontSize: 12 }}>
                            Nothing here is changed or cancelled. Which of a pair to keep is a judgement about a real
                            customer&apos;s order, and the eSIM may already be installed — cancel the duplicate from the
                            orders list once you have decided.
                        </Paragraph>

                        <Table
                            rowKey={(g) => `${g.email}-${g.orders[0].transactionId}`}
                            size="small"
                            dataSource={data.duplicates}
                            columns={columns}
                            pagination={false}
                            locale={{ emptyText: <Empty description="No duplicated checkouts in this window." /> }}
                        />
                    </>
                )}
            </Drawer>
        </>
    );
};

export default DuplicateOrders;
